<template>
	<div>
		<p>Статистика на основі даних з {{ dataLimits?.start }} до {{ dataLimits?.end }}</p>
				
		<div v-if="graph30dLoaded" class="flex gap-1 flex-wrap">
			<button
				v-for="t in stationTypes"
				:key="t"
				:selected="selectedType.type == t.type && selectedType.sensor == t.sensor ? true : null"
				@click="selectedType = t"
				outline
			>
				{{ t.type }}
			</button>
		</div>
		<div v-else class="flex gap-1 flex-wrap">
			<SkeletonLoader class="h-8 w-20"/>
			<SkeletonLoader class="h-8 w-20"/>
		</div>

		<br/>

		<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div>
				<h3 style="margin-bottom: 0.3em;">Усереднені дані по днях</h3>
				<div style="margin-bottom: 1.2em;">за останні 30 днів</div>
				<PlotlyChart 
					v-if="graph30dLoaded"
					:data="dataMonth?.data"
					:layout="{ shapes: dataMonth?.shapes, yaxis: { title: dataMonthTitle, overlaying: 'y2' }, yaxis2: { side: 'right', fixedrange: true, visible: false } }"
					:fixedRange=true
					style="height: 13em;"
				/>
				<SkeletonLoader v-else class="h-[13em] w-full"/>

				<GraphExport 
					:plotly-data="dataMonth?.data" 
					:plotly-layout="{ shapes: dataMonth?.shapes, yaxis: { title: dataMonthTitle, overlaying: 'y2' }, yaxis2: { side: 'right', fixedrange: true, visible: false } }" 
					:title="`Графік забруднення на станції '${props.stationData?.name}'`"
					:subtitle="`Дані EcoCity з ${dataLimits?.start} до ${ dataLimits?.end }. Усереднення по днях.`"
				/>
			</div>
			<div>
				<h3 style="margin-bottom: 0.3em;">По днях тижня</h3>
				<div style="margin-bottom: 1.2em;">за останні 30 днів</div>
				<PlotlyChart 
					v-if="graph30dLoaded"
					:data="weekdayData?.data" 
					:layout="{ shapes: weekdayData?.shapes, yaxis: { title: dataMonthTitle }}"
					:fixedRange=true
					style="height: 13em;"
				/>
				<SkeletonLoader v-else class="h-[13em] w-full"/>

				<GraphExport 
					:plotly-data="weekdayData?.data" 
					:plotly-layout="{ shapes: weekdayData?.shapes, yaxis: { title: dataMonthTitle }}" 
					:title="`Графік забруднення на станції '${props.stationData?.name}' по днях тижня`"
					:subtitle="`Дані EcoCity з ${dataLimits?.start} до ${ dataLimits?.end }.`"
				/>
			</div>
		</div>
		<br/>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div>
				<h3 style="margin-bottom: 0.3em;">По часу доби</h3>
				<div style="margin-bottom: 1.2em;">за останні 7 днів</div>
				<PlotlyChart 
					v-if="graph7dLoaded"
					:data="daytimeData?.data" 
					:layout="{ shapes: daytimeData?.shapes, yaxis: { title: dataMonthTitle }}"
					:title=dataMonthTitle 
					:fixedRange=true
					style="height: 13em;"
				/>
				<SkeletonLoader v-else class="h-[13em] w-full"/>

				<GraphExport 
					:plotly-data="daytimeData?.data" 
					:plotly-layout="{ shapes: daytimeData?.shapes, yaxis: { title: dataMonthTitle }}" 
					:title="`Графік забруднення на станції '${props.stationData?.name}' по часу доби`"
					:subtitle="`Дані EcoCity за останні 7 днів.`"
				/>
			</div>
			<div>
				<h3 style="margin-bottom: 0.3em;">Гістограма значень</h3>
				<div style="margin-bottom: 1.2em;">за останні 7 днів</div>
				<PlotlyChart 
					v-if="graph7dLoaded"
					:data=histogramData 
					:layout="{ yaxis: { title: 'Кількість значень' }, /*xaxis: { title: dataMonthTitle }*/}"
					:title=dataMonthTitle 
					:fixedRange=true
					style="height: 13em;"
				/>
				<SkeletonLoader v-else class="h-[13em] w-full"/>

				<GraphExport 
					:plotly-data="histogramData" 
					:plotly-layout="{ yaxis: { title: 'Кількість значень' }}" 
					:title="`Гістограма значень ${dataMonthTitle} на станції '${props.stationData?.name}'`"
					:subtitle="`Дані EcoCity за останні 7 днів.`"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, defineProps, computed, watch } from 'vue'
import { useColorMode } from '@vueuse/core'
import { useAxios } from '@vueuse/integrations/useAxios'

import GraphExport from '@/components/GraphExport.vue'
import PlotlyChart from '@/components/charts/PlotlyChart.vue'
import SkeletonLoader from '@/components/ui/SkeletonLoader.vue'

import { getLevelsShapes } from '@/modules/PlotlyUtils'

const colorMode = useColorMode()

const props = defineProps({ id: String, stationData: Object })

// const { data: average_48h_1h, isFinished: graph48hLoaded } = useAxios(
// 	`/api/v1/graphs/average_48h_1h?station_id=${props.id}`
// )

const { data: average_30d_1d, isFinished: graph30dLoaded } = useAxios(
	`/api/v1/graphs/average_30d_1d?station_id=${props.id}`
)

const { data: average_7d_20min, isFinished: graph7dLoaded } = useAxios(
	`/api/v1/graphs/average_7d_20min?station_id=${props.id}`
)

const dataLimits = computed(() => {
	if (average_30d_1d.value == null) return null

	return { 
		start: average_30d_1d.value.timestamps.at(0).substring(0, 10),
		end: average_30d_1d.value.timestamps.at(-1).substring(0, 10)
	}
})

const stationTypes = computed(() => {

	let combined = [
		...(
			average_30d_1d.value?.series?.map(s => { return {type: s.type, unit: s.unit, sensor: s.sensor} }) || []
		),
		...(
			average_7d_20min.value?.series?.map(s => { return {type: s.type, unit: s.unit, sensor: s.sensor} }) || []
		),
	]

	const types = combined.reduce((accumulator, current) => {
		if (!accumulator.find(({sensor, type, unit}) => sensor == current.sensor && type == current.type && unit == current.unit)) {
			accumulator.push(current);
		}
		return accumulator;
	}, []);

	return types
})

const selectedType = ref(null)

watch(stationTypes, () => {
	selectedType.value = stationTypes.value[0]
})

const dataMonthTitle = computed(() => {
	return `${selectedType.value?.type} (${selectedType.value?.unit})`
})

const monthSeries = computed(() => {
	const t = selectedType.value

	return average_30d_1d.value?.series?.find(s => s.type == t.type && s.unit == t.unit && s.sensor == t.sensor)
})

const dataMonth = computed(() => {
	if (average_30d_1d.value == null || monthSeries.value == null) return null

	const values = monthSeries.value.values
	const counts = monthSeries.value.counts

	const timestamps = average_30d_1d.value.timestamps

	const shapes = getLevelsShapes(monthSeries.value)

	const data = [
	{
			x: timestamps,
			y: counts,
			marker: { color: "background" },
			type: "bar",
			yaxis: 'y2',
			name: "Кількість",
			hoverinfo: "none"
		},
		{
			x: timestamps,
			y: values.map(v => v?.toFixed(2)),
			marker: { color: "primary" },
			name: monthSeries.value.type
		}
	]

	return { data, shapes }
})

const weekdayData = computed(() => {
	if (average_30d_1d.value == null || monthSeries.value == null) return null

	const timestamps = average_30d_1d.value.timestamps
	const data = monthSeries.value

	// Feature to get the day of the week
	function getDayOfWeek(dateString) {
		const date = new Date(dateString);

		// TODO: Remake to day number
		return date.toLocaleString('uk-UA', { weekday: 'long' });
	}

	// Create an object to save data on the days of the week
	const weekData = {};

	timestamps.forEach((timestamp, index) => {
		if (data.values[index] !== null) {
			const dayOfWeek = getDayOfWeek(timestamp);
			if (!weekData[dayOfWeek]) {
				weekData[dayOfWeek] = [];
			}
			weekData[dayOfWeek].push(data.values[index]);
		}
	});

	// The order of days of the week
	const orderedDays = ["понеділок", "вівторок", "середа", "четвер", "пʼятниця", "субота", "неділя"];
	const daysShortNames = {"понеділок": "пн", "вівторок": "вт", "середа": "ср", "четвер": "чт", "пʼятниця": "пт", "субота": "сб", "неділя": "нд"};

	// Form data for boxing
	const plotData = orderedDays.map(day => ({
		y: weekData[day] || [],
		type: 'box',
		marker: { color: "primary" },
		name: daysShortNames[day]
	}))

	const shapes = getLevelsShapes(data)

	return { data: plotData, shapes }
})

const daytimeData = computed(() => {
	if (average_7d_20min.value == null) return null

	const data = average_7d_20min.value

	const timestamps = data.timestamps.map(timestamp => new Date(timestamp));
	const hours = timestamps.map(date => date.getHours());

	const series = data.series.find(s => 
		s.type == selectedType.value.type &&
		s.unit == selectedType.value.unit &&
		s.sensor == selectedType.value.sensor
	)

	if (series == null) {
		console.error("Series not found")
		return []
	}

	const values = series.values;

	// Grouping values by hour 
	const hourGroups = {};
	hours.forEach((hour, index) => {
		if (!hourGroups[hour]) {
			hourGroups[hour] = [];
		}
		hourGroups[hour].push(values[index]);
	});

	// Data preparation for Plotly
	const plotData = Object.keys(hourGroups).map(hour => ({
		y: hourGroups[hour],
		name: hour,
		marker: { color: "primary" },
		type: "box"
	}))

	const shapes = getLevelsShapes(series)

	return { data: plotData, shapes }
})

const histogramData = computed(() => {
	if (average_7d_20min.value == null) return null

	const series = average_7d_20min.value.series.find(s =>
		s.type == selectedType.value.type &&
		s.unit == selectedType.value.unit &&
		s.sensor == selectedType.value.sensor
	)

	if (series == null) {
		console.error("Series not found")
		return []
	}

	const plotData = [
		{
			x: series.values,
			type: "histogram",
			marker: {
				color: "#bbb7",
				line: {
					color: colorMode.value == 'dark' ? '#bbb' : '#333' ,
					width: 2
				}
			}
		}
	];

	return plotData
})
</script>

<style scoped>

</style>